import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { AdBanner } from "../components/Ads";
import { Sparkles, Send, Loader2, Heart, Moon, Lock, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAILY_LIMIT = 3;

function getDailyCount() {
  const today = new Date().toISOString().slice(0, 10);
  const stored = localStorage.getItem("dn_daily_interpret");
  if (stored) {
    const data = JSON.parse(stored);
    if (data.date === today) return data.count;
  }
  return 0;
}

function incrementDailyCount() {
  const today = new Date().toISOString().slice(0, 10);
  const current = getDailyCount();
  localStorage.setItem("dn_daily_interpret", JSON.stringify({ date: today, count: current + 1 }));
}

export default function InterpretPage() {
  const { isLoggedIn, api } = useAuth();
  const { theme } = useTheme();
  const { t, lang } = useLang();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [situation, setSituation] = useState("");
  const [dreamText, setDreamText] = useState("");
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dailyUsed, setDailyUsed] = useState(getDailyCount());

  const remaining = DAILY_LIMIT - dailyUsed;
  const isLimited = !isPremium && remaining <= 0;

  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textSecondary = isDark ? "text-slate-300/80" : "text-slate-600";
  const textMuted = isDark ? "text-slate-400" : "text-slate-500";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";
  const inputBg = isDark
    ? "bg-slate-950/40 border-white/10 text-white placeholder:text-slate-500"
    : "bg-white/50 border-slate-200 text-slate-900 placeholder:text-slate-400";

  useEffect(() => {
    if (isLoggedIn) {
      api().get("/subscription/check").then((r) => setIsPremium(r.data.is_premium)).catch(() => {});
    }
  }, [isLoggedIn, api]);

  const doInterpret = async () => {
    setLoading(true);
    setError("");
    setInterpretation(null);
    try {
      let res;
      if (isLoggedIn) {
        res = await api().post("/interpret", { dream_text: dreamText, situation: situation || null, lang });
      } else {
        res = await axios.post(`${API}/interpret/guest`, { dream_text: dreamText, situation: situation || null, lang });
      }
      setInterpretation(res.data.interpretation);
      if (!isPremium) {
        incrementDailyCount();
        setDailyUsed(getDailyCount());
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("interpretError"));
    } finally {
      setLoading(false);
    }
  };

  const handleInterpret = () => {
    if (!dreamText.trim() || isLimited) return;
    doInterpret();
  };

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-32 scrollbar-hide overflow-y-auto min-h-screen">
        <div className="mb-6 animate-fadeInUp">
          <h1 className={`font-heading text-3xl font-bold ${textPrimary} ${isDark ? "drop-shadow-lg" : ""}`}>
            {t("interpretTitle")}
          </h1>
          <p className={`${textSecondary} text-sm mt-1`}>{t("interpretSub")}</p>
        </div>

        {/* Daily limit indicator */}
        {!isPremium && (
          <div className={`glass-card p-3 mb-4 flex items-center justify-between animate-fadeIn`}>
            <div className="flex items-center gap-2">
              <Sparkles size={14} className={remaining > 0 ? "text-amber-400" : "text-red-400"} />
              <p className={`text-xs font-medium ${remaining > 0 ? textPrimary : "text-red-400"}`}>
                {remaining > 0 ? `${remaining} ${t("remainingToday")}` : t("dailyLimitTitle")}
              </p>
            </div>
            {remaining <= 1 && !isPremium && (
              <button
                onClick={() => navigate(isLoggedIn ? "/profile" : "/auth")}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${
                  isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-500/15 text-amber-600"
                }`}
              >
                <Crown size={10} /> Premium
              </button>
            )}
          </div>
        )}

        {/* Limit reached modal */}
        {isLimited && (
          <div data-testid="daily-limit-card" className="glass-card p-6 text-center mb-4 animate-fadeInUp">
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <Lock size={24} className="text-red-400" />
            </div>
            <h2 className={`font-heading text-xl font-bold ${textPrimary} mb-2`}>{t("dailyLimitTitle")}</h2>
            <p className={`text-sm ${textMuted} mb-4`}>{t("dailyLimitText")}</p>
            <button
              data-testid="upgrade-premium-button"
              onClick={() => navigate(isLoggedIn ? "/profile" : "/auth")}
              className="btn-gold py-3 px-6 text-sm font-semibold flex items-center justify-center gap-2 mx-auto"
            >
              <Crown size={16} /> {t("dailyLimitCta")}
            </button>
          </div>
        )}

        {/* Situation & Dream form - hidden when limited */}
        {!isLimited && (
          <>
            {/* Zone 1: Situation */}
            <div className="glass-card p-5 mb-4 animate-fadeInUp" style={{ animationDelay: "0.05s" }}>
              <div className="flex items-center gap-2 mb-3">
                <Heart size={14} className={isDark ? "text-pink-400" : "text-pink-500"} />
                <label className={`text-[10px] ${isDark ? "text-pink-400" : "text-pink-500"} uppercase tracking-[0.15em] font-semibold`}>
                  {t("situationLabel")}
                </label>
                <span className={`text-[9px] ${textMuted} ml-auto`}>({t("optional")})</span>
              </div>
              <textarea
                data-testid="situation-textarea"
                value={situation}
                onChange={(e) => setSituation(e.target.value)}
                placeholder={t("situationPlaceholder")}
                className={`w-full border rounded-xl p-4 min-h-[90px] focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/20 outline-none transition-all resize-none text-sm leading-relaxed ${inputBg}`}
              />
            </div>

            {/* Zone 2: Dream */}
            <div className="glass-card p-5 animate-fadeInUp" style={{ animationDelay: "0.1s" }}>
              <div className="flex items-center gap-2 mb-3">
                <Moon size={14} className={isDark ? "text-amber-400" : "text-amber-500"} />
                <label className={`text-[10px] ${labelColor} uppercase tracking-[0.15em] font-semibold`}>
                  {t("describeYourDream")}
                </label>
              </div>
              <textarea
                data-testid="dream-input-textarea"
                value={dreamText}
                onChange={(e) => setDreamText(e.target.value)}
                placeholder={t("dreamPlaceholder")}
                className={`w-full border rounded-xl p-4 min-h-[140px] focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 outline-none transition-all resize-none text-sm leading-relaxed ${inputBg}`}
              />
              <button
                data-testid="interpret-submit-button"
                onClick={handleInterpret}
                disabled={loading || !dreamText.trim()}
                className="mt-3 w-full btn-gold py-3.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 size={18} className="animate-spin" /> {t("interpreting")}</>
                ) : (
                  <><Send size={16} /> {t("interpretBtn")}</>
                )}
              </button>
            </div>
          </>
        )}

        {error && (
          <div data-testid="interpret-error" className="mt-4 glass-card p-4">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {interpretation && (
          <>
            <div data-testid="interpretation-result" className="mt-5 glass-card p-6 animate-fadeInUp">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={14} className="text-purple-500" />
                <p className="text-[10px] text-purple-500 uppercase tracking-[0.15em] font-semibold">
                  {t("interpretation")}
                </p>
              </div>
              <div className={`${textPrimary} text-sm leading-relaxed whitespace-pre-wrap`}>
                {interpretation}
              </div>
            </div>
          </>
        )}

        {/* Single discreet ad - bottom of page, clearly separated from content */}
        {interpretation && !isPremium && (
          <div className="mt-8 mb-4">
            <AdBanner slot="interpret-bottom" />
          </div>
        )}
      </div>

      <BottomNav />
    </AppLayout>
  );
}
